/**
 * V1's `smod_forcevalueinarray` (select_html5.pl:514), ported once.
 *
 * THE RULE: a `<select>` must never display a value it does not hold.
 *
 * Two ways that gets violated, and they look identical to the user:
 *
 *  1. An explicit fallback — `value={stored || 'N'}`. A stored `''` renders as
 *     'N', so the field reads as already-set.
 *  2. A silent one — `value={stored}` where `stored` matches no `<option>`. HTML
 *     falls back to displaying the FIRST option, so a blank over `['Y','N']`
 *     still reads 'N'. Deleting the `||` fallback alone does NOT fix this.
 *
 * Either way the display is a lie, and the lie is self-sustaining: React fires no
 * change event when the user re-picks the option a select is already showing, so
 * nothing stages, nothing saves, and the field never self-heals. It stays wrong
 * for as long as the row exists while every user who opens it reads a good value.
 *
 * That is exactly how `invoice.billfromsystem` stayed blank on ~170 orders and
 * templates for a month (bugfix/template-billfromsystem): the create never seeded
 * it, the dropdown rendered `currentBillFrom || 'CMS'`, and it was invisible from
 * the front end until someone queried the column. It is the same shape as the
 * blank `istank`/`scale`/`otr`/`hot` stamps (#33560).
 *
 * V1 got this right by construction: before rendering a select it forced the
 * stored value into the option array, so an out-of-list value (including a blank)
 * always got its own row and showed as itself.
 *
 * Callers pass the stored value and the option list; if the value is missing from
 * the list, this returns the option to render FIRST so the select shows the truth.
 * A blank gets a blank row — visible as "not set", and one pick away from fixed.
 */
export type SelectOptionLike = string | { value: string; label: string };

/** A stored value normalized the way a `<select>`'s `value` compares it. */
const optionValue = (o: SelectOptionLike): string => (typeof o === 'string' ? o : o.value);

/**
 * The extra `<option>` a select needs so it displays its own stored value, or
 * `null` when the value is already in the list and nothing needs forcing.
 *
 * - blank stored value, no blank option  -> `{ value: '', label: '' }`
 * - legacy out-of-list value (e.g. 'MAS') -> `{ value: 'MAS', label: 'MAS' }`
 * - value already in the list             -> `null`
 *
 * Render it ahead of the real options. Components that already carry a
 * placeholder row (`<option value="">Select…</option>`) or their own stale-value
 * handling should skip the case they already cover — passing a value that is
 * genuinely in `options` returns `null`, so this is safe to call unconditionally.
 */
export function forcedSelectOption(
  value: string | null | undefined,
  options: ReadonlyArray<SelectOptionLike>,
): { value: string; label: string } | null {
  const v = value == null ? '' : String(value);
  if (options.some((o) => optionValue(o) === v)) return null;
  // A blank shows as an EMPTY row, not as the literal "". Anything else shows as
  // itself, which is also the stale-value cue the pro selects highlight.
  return { value: v, label: v };
}

/** True when `value` is absent from `options` — the condition above, on its own. */
export function isForcedSelectValue(
  value: string | null | undefined,
  options: ReadonlyArray<SelectOptionLike>,
): boolean {
  return forcedSelectOption(value, options) !== null;
}
